import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { UserApiService } from '../../shared/services/api/user-api.service';
import { TenantLifecycleService } from '../tenant/tenant-lifecycle.service';
import { ROLES_MANAGE, holdsAnyCapability } from './capabilities';
import { readProfile } from './capability.guard';

const ADMIN_TOOL_KEYS = new WeakMap<CanActivateFn, readonly string[]>();

/** The first admin-tool key the active route needs but `held` lacks, or `null`. */
export function missingAdminToolKeyOn(
  node: ActivatedRouteSnapshot,
  held: ReadonlySet<string>
): string | null {
  for (const guard of node.routeConfig?.canActivate ?? []) {
    const keys = ADMIN_TOOL_KEYS.get(guard as CanActivateFn);
    if (keys && !keys.some((key) => held.has(key))) return keys[0];
  }
  for (const child of node.children) {
    const missing = missingAdminToolKeyOn(child, held);
    if (missing) return missing;
  }
  return null;
}

/** Any of `keys`, else `/home`; bootstraps first because `roles.manage` is held per tenant. */
export function tenantAdminToolGuard(keys: readonly string[]): CanActivateFn {
  const guard: CanActivateFn = async () => {
    const userApi = inject(UserApiService);
    const lifecycle = inject(TenantLifecycleService);
    const router = inject(Router);
    try {
      await firstValueFrom(lifecycle.bootstrapTenantContext());
      const me = await readProfile(userApi);
      if (holdsAnyCapability(me.permissions, keys)) return true;
    } catch {
      // Fall through to the redirect, like every other admin guard.
    }
    return router.createUrlTree(['/home']);
  };
  ADMIN_TOOL_KEYS.set(guard, keys);
  return guard;
}

export const accessRolesAdminGuard: CanActivateFn = tenantAdminToolGuard([ROLES_MANAGE]);
